import { Component } from '@theme/component';

/** @param {string} svg */
function decorateSvg(svg) {
  return svg.replace('<svg ', '<svg aria-hidden="true" style="display:inline;vertical-align:middle" ');
}

/**
 * @typedef {Object} StoreData
 * @property {string} name
 * @property {string} address
 * @property {number} lat
 * @property {number} lng
 * @property {string} [type]
 * @property {string} [contactname]
 * @property {string} [email]
 * @property {string} [phone]
 * @property {string} [website]
 * @property {boolean} [outdoor]
 * @property {boolean} [hunting]
 * @property {boolean} [work]
 */

/**
 * @typedef {Object} HalueStoreLocatorRefs
 * @property {HTMLElement} map - Google Maps container element
 * @property {HTMLElement} body
 * @property {HTMLElement} storeList
 * @property {HTMLElement} filters
 * @property {HTMLFormElement} searchForm
 * @property {HTMLInputElement} searchInput
 * @property {HTMLElement} searchError
 * @property {HTMLButtonElement} mapTabBtn
 * @property {HTMLButtonElement} listTabBtn
 * @property {HTMLButtonElement} locateBtn
 */

/** @type {Promise<void>|null} */
let mapsLoadingPromise = null;


/**
 * @param {string} apiKey
 * @returns {Promise<void>}
 */
function loadGoogleMaps(apiKey) {
  if (typeof google !== 'undefined' && google.maps) {
    return Promise.resolve();
  }

  if (mapsLoadingPromise) return mapsLoadingPromise;

  mapsLoadingPromise = new Promise((resolve) => {
    window.__halueStoreLocatorMapsReady = () => {
      resolve();
    };

    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&callback=__halueStoreLocatorMapsReady`;
    script.async = true;
    script.defer = true;
    document.head.appendChild(script);
  });

  return mapsLoadingPromise;
}

/**
 * @param {string} line
 * @returns {string[]}
 */
function parseCsvRow(line) {
  /** @type {string[]} */
  const result = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const char = line[i] ?? '';
    if (char === '"') {
      if (inQuotes && line[i + 1] === '"') {
        current += '"'; // avoid to include escape chart \ for quote
        i++;
      } else {
        inQuotes = !inQuotes; // start double quote. It's in quotes. Hard to read but inQuotes is false for sure
      }
    } else if (char === ',' && !inQuotes) { // Handle the end
      result.push(current.trim());
      current = '';
    } else { // Both in and out of quotes. It could be the start of a cel
      current += char;
    }
  }
  result.push(current.trim());
  return result;
}

/**
 * @param {string} csv
 * @returns {StoreData[]}
 */
function parseStoresCsv(csv) {
  const lines = csv.trim().split('\n');
  const headerLine = lines[0];
  if (!headerLine) return [];

  const headers = parseCsvRow(headerLine).map(h => h.trim().toLowerCase());
  const nameIdx = headers.indexOf('name');
  const addressIdx = headers.indexOf('address');
  const latIdx = headers.indexOf('lat');
  const lngIdx = headers.indexOf('lang'); // CSV column is named 'lang', not 'lng'
  const typeIdx = headers.indexOf('type');
  const contactIdx = headers.indexOf('contactname');
  const emailIdx = headers.indexOf('email');
  const phoneIdx = headers.indexOf('phone');
  const websiteIdx = headers.indexOf('website');
  const outdoorIdx = headers.indexOf('outdoor');
  const huntingIdx = headers.indexOf('hunting');
  const workIdx = headers.indexOf('work');

  /** @type {StoreData[]} */
  const stores = [];

  for (let i = 1; i < lines.length; i++) {
    const line = lines[i] ?? '';
    if (!line.trim()) continue;
    const values = parseCsvRow(line);
    const lat = parseFloat(values[latIdx] ?? '');
    const lng = parseFloat(values[lngIdx] ?? '');
    if (isNaN(lat) || isNaN(lng)) continue;
    stores.push({
      name: values[nameIdx] ?? '',
      address: values[addressIdx] ?? '',
      lat,
      lng,
      type: values[typeIdx],
      contactname: values[contactIdx],
      email: values[emailIdx],
      phone: values[phoneIdx],
      website: values[websiteIdx],
      outdoor: (values[outdoorIdx] ?? '').toUpperCase() === 'TRUE',
      hunting: (values[huntingIdx] ?? '').toUpperCase() === 'TRUE',
      work: (values[workIdx] ?? '').toUpperCase() === 'TRUE'
    });
  }

  return stores;
}

/** @extends {Component<HalueStoreLocatorRefs>} */
class HalueStoreLocator extends Component {
  /** @type {any} */
  #map = null;
  #zoom = 9;
  /** @type {any} */
  #infoWindow = null;
  /** @type {any[]} */
  #markers = [];
  /** @type {any} */
  #clusterer = null;
  #selectedIndex = -1;
  /** @type {StoreData[]} */
  #stores = [];
  /** @type {Record<string, string>} */
  #icons = {};
  /** @type {any} */
  #autocomplete = null;
  /** @type {any} */
  #autocompleteListener = null;
  /** @type {string|null} */
  #activeTypeFilter = null;
  /** @type {Set<string>} */
  #activeActivityFilters = new Set();
  #boundHandleSearch = this.handleSearch.bind(this);
  #boundHandleListClick = this.#handleListClick.bind(this);
  #boundShowMap = this.#showMapTab.bind(this);
  #boundShowList = this.#showListTab.bind(this);
  #boundLocateMe = this.#locateMe.bind(this);
  #boundHandleFilterChange = this.#handleFilterChange.bind(this);
  #boundHandleFilterToggle = this.#handleFilterToggle.bind(this);

  connectedCallback() {
    super.connectedCallback();
    this.#icons = {
      email: decorateSvg(this.dataset.iconEmail ?? ''),
      phone: decorateSvg(this.dataset.iconPhone ?? ''),
      globe: decorateSvg(this.dataset.iconGlobe ?? ''),
      tag: decorateSvg(this.dataset.iconTag ?? ''),
      cockade: decorateSvg(this.dataset.iconCockade ?? ''),
      listCheck: decorateSvg(this.dataset.iconListCheck ?? ''),
    };
    this.refs.searchForm?.addEventListener('submit', this.#boundHandleSearch);
    this.refs.storeList?.addEventListener('click', this.#boundHandleListClick);
    this.refs.mapTabBtn?.addEventListener('click', this.#boundShowMap);
    this.refs.listTabBtn?.addEventListener('click', this.#boundShowList);
    this.refs.locateBtn?.addEventListener('click', this.#boundLocateMe);
    this.refs.filters?.addEventListener('change', this.#boundHandleFilterChange);
    this.refs.filters?.querySelectorAll('details').forEach(d => d.addEventListener('toggle', this.#boundHandleFilterToggle));
    this.#initMap();
  }

  disconnectedCallback() {
    this.refs.searchForm?.removeEventListener('submit', this.#boundHandleSearch);
    this.refs.storeList?.removeEventListener('click', this.#boundHandleListClick);
    this.refs.mapTabBtn?.removeEventListener('click', this.#boundShowMap);
    this.refs.listTabBtn?.removeEventListener('click', this.#boundShowList);
    this.refs.locateBtn?.removeEventListener('click', this.#boundLocateMe);
    this.refs.filters?.removeEventListener('change', this.#boundHandleFilterChange);
    this.refs.filters?.querySelectorAll('details').forEach(d => d.removeEventListener('toggle', this.#boundHandleFilterToggle));
    this.#autocompleteListener?.remove();
    super.disconnectedCallback();
  }

  async #initMap() {
    const apiKey = this.dataset.apiKey;
    if (!apiKey || !this.refs.map) return;

    try {
      [, this.#stores] = await Promise.all([loadGoogleMaps(apiKey), this.#fetchStores()]);
      this.#renderMap();
    } catch (error) {
      console.error('HalueStoreLocator: failed to initialize', error);
    }
  }

  /** @returns {Promise<StoreData[]>} */
  async #fetchStores() {
    const url = this.dataset.storesUrl;
    if (!url) return [];
    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      return parseStoresCsv(await response.text());
    } catch (error) {
      console.error('HalueStoreLocator: failed to fetch stores', error);
      return [];
    }
  }

  #renderMap() {
    if (!this.refs.map) return;

    this.#zoom = parseInt(this.dataset.zoom ?? '', 9) || 9;
    const centerLat = parseFloat(this.dataset.centerLat ?? '');
    const centerLng = parseFloat(this.dataset.centerLng ?? '');
    const hasExplicitCenter = !isNaN(centerLat) && !isNaN(centerLng);

    const bounds = new google.maps.LatLngBounds();

    this.#map = new google.maps.Map(this.refs.map, {
      zoom: this.#zoom,
      center: hasExplicitCenter ? { lat: centerLat, lng: centerLng } : { lat: 46.5, lng: 10.5 },
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: true,
      zoomControl: true
    });

    this.#initAutocomplete();
    this.#infoWindow = new google.maps.InfoWindow();

    this.#stores.forEach((store, i) => {
      const position = { lat: store.lat, lng: store.lng };
      bounds.extend(position);

      const marker = new google.maps.Marker({
        position,
        title: store.name,
        icon: {
          url: this.dataset.markerIcon ?? '',
          scaledSize: new google.maps.Size(32, 40),
          anchor: new google.maps.Point(16, 40)
        }
      });

      this.#markers.push(marker);
      marker.addListener('click', () => this.#selectStore(i, true));
    });

    if (!hasExplicitCenter) {
      this.#map?.fitBounds(bounds);
    }

    this.#map.addListener('idle', () => this.#updateStoreList());
    this.#requestGeolocation();
    this.#initClusterer();
  }

  #initClusterer() {
    const MarkerClusterer = /** @type {any} */ (window).markerClusterer?.MarkerClusterer;
    if (!MarkerClusterer) {
      this.#markers.forEach(m => m.setMap(this.#map));
      return;
    }
    const renderer = {
      render({ count, position }) {
        const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40">
          <circle cx="20" cy="20" r="20" fill="#000"/>
          <text x="20" y="25" text-anchor="middle" font-family="sans-serif" font-size="13" font-weight="bold" fill="#fff">${count}</text>
        </svg>`;
        return new google.maps.Marker({
          position,
          icon: {
            url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
            scaledSize: new google.maps.Size(40, 40),
            anchor: new google.maps.Point(20, 20)
          },
          zIndex: 1000 + count
        });
      }
    };
    this.#clusterer = new MarkerClusterer({ map: this.#map, markers: this.#markers, renderer });
  }

  #updateStoreList() {
    if (!this.refs.storeList || !this.#map) return;
    const bounds = this.#map.getBounds();
    if (!bounds) return;
    const items = this.#stores
      .map((store, index) => ({ store, index }))
      .filter(({ store }) => bounds.contains({ lat: store.lat, lng: store.lng }) && this.#storePassesFilter(store));
    this.refs.storeList.innerHTML = this.#buildStoreListHTML(items);
  }

  /**
   * @param {StoreData} store
   * @returns {boolean}
   */
  #storePassesFilter(store) {
    if (this.#activeTypeFilter && store.type !== this.#activeTypeFilter) return false;
    if (this.#activeActivityFilters.size > 0) {
      const hasActivity = [...this.#activeActivityFilters].some(act => store[/** @type {keyof StoreData} */ (act)] === true);
      if (!hasActivity) return false;
    }
    return true;
  }

  /** @param {Event} event */
  #handleFilterToggle(event) {
    const opened = /** @type {HTMLDetailsElement} */ (event.target);
    if (!opened.open) return;
    this.refs.filters?.querySelectorAll('details').forEach(d => {
      if (d !== opened) d.open = false;
    });
  }

  /** @param {Event} event */
  #handleFilterChange(event) {
    const target = /** @type {HTMLInputElement} */ (event.target);
    if (target.name === 'sl-type') {
      this.#activeTypeFilter = target.value || null;
    } else if (target.name === 'sl-activity') {
      if (target.checked) {
        this.#activeActivityFilters.add(target.value);
      } else {
        this.#activeActivityFilters.delete(target.value);
      }
    }
    this.#applyMarkersVisibility();
    this.#updateStoreList();
  }

  #applyMarkersVisibility() {
    if (this.#clusterer) {
      this.#clusterer.clearMarkers();
      const visibleMarkers = this.#markers.filter((_, i) => {
        const store = this.#stores[i];
        return store ? this.#storePassesFilter(store) : false;
      });
      this.#clusterer.addMarkers(visibleMarkers);
    } else {
      this.#markers.forEach((m, i) => {
        const store = this.#stores[i];
        m.setMap(store && this.#storePassesFilter(store) ? this.#map : null);
      });
    }
  }

  /**
   * @param {{ store: StoreData, index: number }[]} items
   * @returns {string}
   */
  #buildStoreListHTML(items) {
    if (items.length === 0) {
      const msg = this.dataset.listEmpty ?? 'No stores visible in this area.';
      return `<p class="halue-store-locator__list-empty">${msg}</p>`;
    }
    const seeOnMap = this.dataset.seeOnMap ?? 'View on map';
    const directionsLabel = this.dataset.directions ?? 'Get directions';
    const productsAbout = this.dataset.productsAbout ?? 'Collections:';

    return `<ul class="halue-store-locator__list-items">${items.map(({ store, index }) => {
      const directionsUrl = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(store.address)}`;
      const activities = this.#getActivities(store);
      return `<li class="halue-store-locator__list-item${index === this.#selectedIndex ? ' is-selected' : ''}">
        <strong class="halue-store-locator__list-item-name">${store.name}</strong>
        <p class="halue-store-locator__list-item-address">${store.address}</p>
        ${this.#buildTypeHTML(store.type, 'halue-store-locator__list-item')}
        ${activities ? `<p class="halue-store-locator__list-item-activities"><span class="halue-store-locator__list-item-label">${this.#icons.listCheck}</span> ${productsAbout} ${activities}</p>` : ''}
        ${this.#buildContactHTML(store, 'halue-store-locator__list-item')}
        <div class="halue-store-locator__list-item-button-wrapper">
          <button type="button" class="button halue-store-locator__list-item-button" data-store-index="${index}">${seeOnMap}</button>
          <div><a class="halue-store-locator__list-item-direction" href="${directionsUrl}" target="_blank" rel="noopener">${directionsLabel}</a></div>
        </div>
      </li>`}).join('')}
    </ul>`;
  }

  /**
   * @param {number} index
   * @param {boolean} fromMap
   */
  #selectStore(index, fromMap) {
    this.#selectedIndex = index;
    const store = this.#stores[index];
    const marker = this.#markers[index];
    if (!store || !marker || !this.#map || !this.#infoWindow) return;

    this.#infoWindow.setContent(this.#buildInfoContent(store));
    this.#infoWindow.open(this.#map, marker);

    if (!fromMap) {
      this.#map.panTo({ lat: store.lat, lng: store.lng });
    }

    if (this.refs.storeList) {
      this.refs.storeList.querySelectorAll('.halue-store-locator__list-item')
        .forEach(el => el.classList.remove('is-selected'));
      const item = this.refs.storeList
        .querySelector(`[data-store-index="${index}"]`)
        ?.closest('.halue-store-locator__list-item');
      if (item) {
        item.classList.add('is-selected');
        if (fromMap) item.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }
  }

  /** @param {Event} event */
  #handleListClick(event) {
    const target = /** @type {HTMLElement|null} */ (event.target);
    const button = target?.closest('[data-store-index]');
    if (!button) return;
    const index = parseInt(/** @type {HTMLElement} */(button).dataset.storeIndex ?? '', 10);
    if (!isNaN(index)) this.#selectStore(index, false);
  }

  #initAutocomplete() {
    if (!this.refs.searchInput) return;
    this.#autocomplete = new google.maps.places.Autocomplete(this.refs.searchInput, {
      fields: ['geometry', 'name']
    });
    this.#autocompleteListener = this.#autocomplete.addListener('place_changed', () => {
      this.#handlePlaceChanged();
    });
  }

  #handlePlaceChanged() {
    const place = this.#autocomplete?.getPlace();
    if (!place?.geometry?.location) {
      this.#setSearchError(true);
      return;
    }
    this.#setSearchError(false);
    this.#map?.setCenter(place.geometry.location);
    this.#map?.setZoom(this.#zoom);
  }

  /** @param {SubmitEvent} event */
  handleSearch(event) {
    event.preventDefault();
    this.#handlePlaceChanged();
  }

  #showMapTab() {
    this.refs.body?.removeAttribute('data-mobile-view');
    this.refs.mapTabBtn?.classList.add('is-active');
    this.refs.listTabBtn?.classList.remove('is-active');
  }

  #showListTab() {
    this.refs.body?.setAttribute('data-mobile-view', 'list');
    this.refs.listTabBtn?.classList.add('is-active');
    this.refs.mapTabBtn?.classList.remove('is-active');
  }

  /** @param {boolean} visible */
  #setSearchError(visible) {
    if (!this.refs.searchError) return;
    if (visible) {
      this.refs.searchError.removeAttribute('hidden');
    } else {
      this.refs.searchError.setAttribute('hidden', '');
    }
  }

  #locateMe() {
    const map = this.#map;
    if (!navigator.geolocation || !map) return;
    navigator.geolocation.getCurrentPosition(
      ({ coords }) => {
        map.setCenter({ lat: coords.latitude, lng: coords.longitude });
        map.setZoom(this.#zoom);
      },
      () => { }
    );
  }

  #requestGeolocation() {
    this.#locateMe();
  }

  /**
   * @param {StoreData} store
   * @returns {string}
   */
  #getActivities(store) {
    return /** @type {(keyof StoreData)[]} */ (['outdoor', 'hunting', 'work'])
      .filter(act => store[act] === true)
      .map(act => String(act).charAt(0).toUpperCase() + String(act).slice(1))
      .join(', ');
  }

  /**
   * @param {StoreData} store
   * @param {string} prefix
   * @returns {string}
   */
  #buildContactHTML(store, prefix) {
    let html = '';

    if (store.email) html += `<p class="${prefix}-email"><span class="${prefix}-label">${this.#icons.email}</span> ${store.email}</p>`;
    if (store.phone) html += `<p class="${prefix}-phone"><span class="${prefix}-label">${this.#icons.phone}</span> ${store.phone}</p>`;
    if (store.website) {
      const href = store.website.startsWith('http') ? store.website : `https://${store.website}`;
      html += `<p class="${prefix}-website"><span class="${prefix}-label">${this.#icons.globe}</span> <a href="${href}" target="_blank" rel="noopener">${store.website}</a></p>`;
    }

    return `<div class="${prefix}-contact-wrapper">${html}</div>`;
  }

  /**
   * @param {StoreData} store
   * @returns {string}
   */
  /**
   * @param {string|undefined} type
   * @param {string} prefix
   */
  #buildTypeHTML(type, prefix) {
    if (!type) return '';
    if (type === 'store') {
      const label = this.#resolveTypeLabel(type);
      return `<p class="${prefix}-type"><span class="${prefix}-label">${this.#icons.cockade}</span> ${label}</p>`;
    }
    const label = this.#resolveTypeLabel(type);
    return `<p class="${prefix}-type"><span class="${prefix}-label">${this.#icons.tag}</span> ${label}</p>`;
  }

  /** @param {string|undefined} type */
  #resolveTypeLabel(type) {
    if (!type) return '';
    const key = `type${type.charAt(0).toUpperCase()}${type.slice(1)}`;
    return this.dataset[key] ?? type;
  }

  /** @param {StoreData} store */
  #buildInfoContent(store) {
    const activities = this.#getActivities(store);
    const productsAbout = this.dataset.productsAbout ?? 'Collections:';
    return `<div class="halue-store-locator__info">
      <strong class="halue-store-locator__info-name">${store.name}</strong>
      <p class="halue-store-locator__info-address" style="margin-bottom: 1rem;">${store.address}</p>
      ${this.#buildTypeHTML(store.type, 'halue-store-locator__info')}
      ${activities ? `<p class="halue-store-locator__info-activities"><span class="halue-store-locator__info-label">${this.#icons.listCheck}</span> ${productsAbout} ${activities}</p>` : ''}
      <div style="margin-top:1rem; margin-bottom: 1rem">
        ${this.#buildContactHTML(store, 'halue-store-locator__info')}
      </div>
    </div>`;
  }
}

customElements.define('halue-store-locator', HalueStoreLocator);
